/*
  Pricer configurator.

  PRICING DATA: real numbers from Colin's spreadsheet, loaded from
  assets/pricer-real-data.js (must be loaded before this file — both script
  tags are emitted together, either by the `pricer` block in
  snippets/product-info.liquid or by sections/pricer.liquid when the
  configurator is used standalone). One piece is still a placeholder: the base
  starting price itself (Colin's sheet only had the adders, not the base
  price they get added to) — see the comment on PRICER_REAL_DATA.base in
  that file. Everything else here is real.

  ASSUMPTION NOT YET CONFIRMED WITH COLIN: which axis of the base-adder
  table is width vs height (the sheet didn't label it, and the table isn't
  symmetric, so this matters). Assumed header row = width, row label =
  height, matching the order the form asks for them. If that's backwards,
  fix it in baseAdderFor() below only — the data itself doesn't need to change.

  PRICING BACKEND: Add to Cart calls the stencil-pricer-backend app's
  /apps/pricer/quote endpoint (via Shopify's app proxy) to get a variant
  priced exactly right for the chosen width/height/qty, then submits THAT
  variant instead of the product's base one — this is what makes the price
  actually charged match the price shown. Requires that backend app to be
  installed on whichever store this section runs on (the app proxy path
  only resolves on stores where the app is installed); if it's not
  installed, the fetch below will fail and Add to Cart is correctly blocked
  rather than silently charging the wrong price.
*/
(function () {
  if (customElements.get('pricer-app')) return;

  var DATA = window.PRICER_REAL_DATA;
  var MAX_REAL_QTY = 1005; // last non-sentinel row in the quantity-discount table
  var MIN_DIM = 2;
  var MAX_DIM = 48; // matches the real base-adder table's range (row/col 49 are degenerate sentinels) — must stay in sync with pricing-calculator.server.js's own MIN_DIM/MAX_DIM on the backend

  function baseAdderFor(width, height){
    var w = Math.min(MAX_DIM, Math.max(MIN_DIM, Math.round(width)));
    var h = Math.min(MAX_DIM, Math.max(MIN_DIM, Math.round(height)));
    return DATA.baseAdders[h - 2][w - 2];
  }
  function qtyColFor(area){
    var headers = DATA.qtyHeaders;
    var col = 0;
    for (var i = 0; i < headers.length - 1; i++) { // exclude the trailing 9999 sentinel column
      if (headers[i] <= area) col = i;
    }
    return col;
  }
  function qtyAddFor(q, col){
    var labels = DATA.qtyLabels;
    var table = DATA.qtyDiscount;
    var qty = Math.min(MAX_REAL_QTY, Math.max(1, q));
    var exact = labels.indexOf(qty);
    if (exact !== -1) return table[exact][col];
    // Not every quantity has its own row past 100 (steps of 5) — interpolate
    // linearly between the two nearest real rows for a smooth in-between price.
    for (var i = 0; i < labels.length - 1; i++) {
      if (labels[i] < qty && qty < labels[i + 1] && labels[i + 1] <= MAX_REAL_QTY) {
        var t = (qty - labels[i]) / (labels[i + 1] - labels[i]);
        return table[i][col] + t * (table[i + 1][col] - table[i][col]);
      }
    }
    return table[labels.indexOf(MAX_REAL_QTY)][col];
  }
  function quote(w, h, q){
    if (!w || !h) return null;
    var area = w * h;
    var total0 = DATA.base + baseAdderFor(w, h);
    var total = total0 + qtyAddFor(q, qtyColFor(area));
    var unit = Math.round((total / q) * 100) / 100;
    return { unit: unit, total: unit * q, single: Math.round(total0 * 100) / 100 };
  }
  /* The pricing tables are in the shop's own currency, but a visitor may be
     browsing in another one — Shopify converts at the cart. Showing a hardcoded
     "$" meant a customer on, say, PHP saw "$416.01" here and a peso total at
     checkout. Convert and format with the active currency instead, falling back
     to the old behaviour if the storefront doesn't expose one. */
  function money(n){
    var c = window.Shopify && window.Shopify.currency;
    var rate = c && parseFloat(c.rate);
    var code = c && c.active;
    if (!code || !rate || !isFinite(rate)) return '$' + n.toFixed(2);
    var converted = n * rate;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: code
      }).format(converted);
    } catch (e) {
      return converted.toFixed(2) + ' ' + code;
    }
  }
  function snap4(n){ return Math.round(n * 4) / 4; }

  /* The button's resting label, kept in one place so the busy states can't
     accidentally become permanent. */
  var ATC_LABEL = 'Add to Cart';

  /* Shopify's ceiling for a line-item property file upload. */
  var MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

  /* Ceiling for the artwork embedded in the designer SVG. Base64 is ~1 byte per
     character, so this is effectively the finished file's size. Held well below
     MAX_UPLOAD_BYTES because the SVG is a THIRD attachment — the original and
     the cropped preview share the same request. */
  var SVG_ARTWORK_BUDGET = 12 * 1024 * 1024;

  /* Matches the number of `properties[_Reference File N]` inputs rendered in
     pricer-app.liquid — five fixed, individually named slots rather than one
     multi-file input under a single property name (see the comment there
     for why). Keep these two in sync if the count ever changes. */
  var MAX_REF_FILES = 5;

  /* THE definition of "the artwork fits this size" — used for the upload
     message, the warning, and the Add to Cart gate alike.

     It asks the only question that matters: at the chosen width, does the
     artwork's own height come out as the height they ordered? Because the crop
     dialog derives its height exactly this way, the two can no longer
     contradict each other.

     This replaced a 15% ratio tolerance, which called artwork a match when it
     was up to 1.8" out on a 12" stencil — the app said "fits your 12 × 12″"
     and the dialog then offered "Use 12 × 12.5″". Quarter-inch agreement (what
     snap4 rounds to, and the granularity these stencils are cut at) is the
     honest bar. */
  function fitsSize(artRatio, w, h){
    if (!artRatio || !w || !h) return false;
    return snap4(w / artRatio) === snap4(h);
  }

  /* .ai files usually contain a PDF stream (Illustrator's "Create PDF Compatible
     File", on by default), so PDF.js can render them too. Their MIME type is
     unreliable — often application/postscript or empty — hence the extension
     check alongside it. */
  function isPdfLike(f){
    var name = (f.name || '').toLowerCase();
    return f.type === 'application/pdf' || /\.(pdf|ai)$/.test(name);
  }

  var pdfJsPromise = null;
  function loadPdfJs(src, workerSrc){
    if (pdfJsPromise) return pdfJsPromise;
    pdfJsPromise = new Promise(function (resolve, reject) {
      if (!src) { reject(new Error('pdf.js asset url missing')); return; }
      if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
      var s = document.createElement('script');
      s.src = src;
      s.onload = function () {
        if (!window.pdfjsLib) { reject(new Error('pdf.js failed to define')); return; }
        if (workerSrc) window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
        resolve(window.pdfjsLib);
      };
      s.onerror = function () { reject(new Error('pdf.js failed to load')); };
      document.head.appendChild(s);
    });
    return pdfJsPromise;
  }
  function ratioLabel(r){
    var pairs = [[1,1],[4,3],[3,2],[16,9],[2,3],[3,4],[9,16]];
    var best = pairs[0], d = 1e9;
    pairs.forEach(function (p) { var dd = Math.abs(r - p[0] / p[1]); if (dd < d) { d = dd; best = p; } });
    return best[0] + ':' + best[1];
  }
  var FILE_ICON = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56"><rect width="56" height="56" fill="%23FFDE1B"/><text x="28" y="34" font-size="12" text-anchor="middle" font-family="sans-serif" font-weight="bold">FILE</text></svg>';

  class PricerApp extends HTMLElement {
    connectedCallback(){
      this.state = { w: null, h: null, qty: 1, artUploaded: false, sendLater: false, artW: null, artH: null, sizeTouched: false, qtyTouched: false, cropRatio: null, savedCrop: null, fullSrc: null, fitMode: null, previewable: false, mismatch: false, pausedUpload: null, sizeExceedsMax: false, refFiles: [] };
      this.crop = { x: 0, y: 0, w: 0, h: 0 };
      this.anchor = 'w';
      this.dragMode = null;
      this.dragStart = null;

      this.bindEls();
      this.bindEvents();

      /* The crop modal is position:fixed and must live directly on <body>.
         Left inside the section, a theme animation utility applying transform
         to an ancestor would silently break fixed positioning and make the
         modal render off-screen while its dark backdrop still shows. */
      if (this.el.cropMask && this.el.cropMask.parentNode !== document.body) {
        document.body.appendChild(this.el.cropMask);
      }

      var defaultPreset = this.querySelector('.preset[data-w="12"]');
      if (defaultPreset) defaultPreset.classList.add('selected');
      this.state.w = 12; this.state.h = 12;
      this.render();
    }

    $(ref){ return this.querySelector('[data-ref="' + ref + '"]'); }
    $$(sel){ return Array.prototype.slice.call(this.querySelectorAll(sel)); }

    bindEls(){
      var refs = ['numSize','numQty','numArt','customFields','cw','ch','sizeNote','oversizeNote',
        'qty','qtyMinus','qtyPlus','tierNudge','savingsToggle','savingsTable',
        'uploadZone','fileInput','croppedInput','designerInput','sampleBtn','laterLink','laterNote','uploadSuccess','thumb','upStatus','fname','uploadErrorNote',
        'refFilesBtn','refFilesPicker','refFilesList',
        'cropOpen','swapFile','noPreviewNote','noPreviewAck','noPreviewSize','aspectFlag','aspectAck','ackSize','ackNatural','aspectUndo','artRatio','sheetRatio','aspectKeep','aspectMatch',
        'detailsToggle','detailsBody','dname',
        'total','perUnit','saveLine','atcBtn','atcNote',
        'cropMask','cropModal','cropHint','cropImg','cropStage','cropBox','dimW','dimH','cropCancel','cropConfirm','chosenSize',
        'propWidth','propHeight','propArtStatus','propFitMode'];
      this.el = {};
      var self = this;
      refs.forEach(function (r) { self.el[r] = self.$(r); });
    }

    bindEvents(){
      var self = this;

      this.$$('.preset').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.state.sizeTouched = true;
          self.$$('.preset').forEach(function (b) { b.classList.remove('selected'); });
          btn.classList.add('selected');
          var custom = btn.dataset.custom;
          self.el.customFields.classList.toggle('open', !!custom);
          self.state.sizeExceedsMax = false; // every path below lands on an in-range size
          if (custom) {
            self.setCustomSize(parseFloat(self.el.cw.value), parseFloat(self.el.ch.value));
          } else {
            self.state.w = +btn.dataset.w;
            self.state.h = +btn.dataset.h;
          }
          self.sizeChanged();
        });
      });
      ['cw','ch'].forEach(function (ref) {
        /* input: track keystrokes live for the price preview WITHOUT clamping-
           and-rewriting the field on every character. Rewriting mid-typing was
           actively hostile to typing itself — e.g. typing "14" started with "1",
           which alone is below MIN_DIM and got immediately clamped-and-written
           back as "2", so the field showed 2 instead of 1; the same rewrite also
           stomped any decimal point the customer had just typed, since the
           written-back value is a plain number with no ".". Flagged by Colin,
           2026-08-11. Range clamping only happens once they're done editing
           (blur, below) — same pattern as a native <input type=number>, which
           only flags out-of-range on submit, not per keystroke. */
        self.el[ref].addEventListener('input', function () {
          self.state.sizeTouched = true;
          var w = parseFloat(self.el.cw.value), h = parseFloat(self.el.ch.value);
          self.state.w = Number.isFinite(w) ? w : null;
          self.state.h = Number.isFinite(h) ? h : null;
          /* Tracked separately from state.w/h because those get silently
             clamped to MAX_DIM on blur (setCustomSize) — without its own
             flag, the "want something bigger?" note would vanish the
             instant the field re-writes itself, right when it's most
             useful (explaining why 60 just became 48). */
          self.state.sizeExceedsMax = (Number.isFinite(w) && w > MAX_DIM) || (Number.isFinite(h) && h > MAX_DIM);
          self.sizeChanged();
        });
        /* focusout finalizes the pair once the customer is actually done with
           BOTH fields, not the instant either one loses focus. Width and
           Height are one logical entry: tabbing/clicking from Width straight
           into Height is normal mid-entry navigation, not "done" — clamping
           there was Colin's next report after the input-event fix, 2026-08-11
           (typing "1" then moving to Height turned it into "2" before he'd
           even finished).

           This checks document.activeElement on a deferred tick rather than
           e.relatedTarget, which was the first attempt here — relatedTarget
           is only reliably populated for keyboard-driven (Tab) focus moves.
           Colin reproduced the original bug again while testing in Chrome's
           mobile device emulation, where tapping directly into the next
           field left relatedTarget unset, so the "still working within the
           pair" check silently failed and the premature clamp came right
           back. activeElement has no such gap — it reflects real focus
           however it got there. The tradeoff is the deferred check can't run
           before a synchronous click elsewhere (e.g. Add to Cart) reads
           state.w/h, so handleAddToCart() below has its own synchronous
           clamp as a backstop — this one is purely about not fighting the
           customer's fields while they're still visibly mid-entry. */
        self.el[ref].addEventListener('focusout', function () {
          setTimeout(function () {
            var active = document.activeElement;
            if (active === self.el.cw || active === self.el.ch) return;
            self.setCustomSize(parseFloat(self.el.cw.value), parseFloat(self.el.ch.value));
            self.sizeChanged();
          }, 0);
        });
      });

      this.el.qtyMinus.addEventListener('click', function () { self.setQty(self.state.qty - 1); });
      this.el.qtyPlus.addEventListener('click', function () { self.setQty(self.state.qty + 1); });
      this.el.qty.addEventListener('input', function (e) { self.setQty(parseInt(e.target.value, 10)); });
      if (this.el.dname) {
        /* The design name is printed on the designer SVG's artboard, and it can
           be typed long after the crop was baked. Same staleness rule as qty:
           mark it, and Add to Cart regenerates once. */
        this.el.dname.addEventListener('input', function () { self._svgStale = true; });
      }
      this.el.savingsToggle.addEventListener('click', function () {
        var open = self.el.savingsTable.classList.toggle('open');
        self.el.savingsToggle.textContent = open ? 'Hide volume savings' : 'See volume savings';
      });

      /* Optional — the demo button was removed from the markup, but useSampleArt
         is kept so it can be re-enabled by adding the button back. */
      if (this.el.sampleBtn) this.el.sampleBtn.addEventListener('click', function () { self.useSampleArt(); });

      if (this.el.refFilesBtn) {
        this.el.refFilesBtn.addEventListener('click', function () { self.el.refFilesPicker.click(); });
        this.el.refFilesPicker.addEventListener('change', function (e) {
          self.onRefFilesChosen(e.target.files);
          e.target.value = ''; // lets picking the exact same file again still fire 'change'
        });
      }
      this.el.laterLink.addEventListener('click', function () { self.toggleSendLater(); });

      var openPicker = function () { self.el.fileInput.click(); };
      this.el.uploadZone.addEventListener('click', openPicker);
      this.el.swapFile.addEventListener('click', openPicker);
      this.el.fileInput.addEventListener('change', function (e) { self.onFileChosen(e.target.files[0]); });

      /* "Stretch to fit" is a real decision, not a dismissal — record it so the
         order carries the fact that the customer knowingly accepted distortion,
         instead of looking identical to a clean order. */
      this.el.aspectKeep.addEventListener('click', function () {
        self.state.fitMode = 'stretch';
        /* Via checkAspect, not by hiding the flag directly — it's what swaps the
           warning for the standing reminder. */
        self.checkAspect();
        self.render();
      });
      /* Locked to the chosen size: crops the ARTWORK to fit the order, rather
         than resizing the order to fit the artwork. */
      this.el.aspectUndo.addEventListener('click', function () {
        /* Straight into the locked crop — the customer already knows what they
           want, so don't make them pick from a menu first. */
        self.openCrop();
      });
      this.el.aspectMatch.addEventListener('click', function () {
        self.openCrop();
      });
      this.el.cropOpen.addEventListener('click', function () { self.openCrop(); });

      if (this.el.noPreviewAck) {
        this.el.noPreviewAck.addEventListener('change', function () {
          self.state.fitMode = this.checked ? 'unverified' : null;
          self.render();
        });
      }

      this.el.dimW.addEventListener('input', function () { self.anchor = 'w'; self.updateComputed(); });
      this.el.dimH.addEventListener('input', function () { self.anchor = 'h'; self.updateComputed(); });

      this.el.cropStage.addEventListener('pointerdown', function (e) { self.onCropPointerDown(e); });
      this.el.cropStage.addEventListener('pointermove', function (e) { self.onCropPointerMove(e); });
      ['pointerup', 'pointercancel'].forEach(function (ev) {
        self.el.cropStage.addEventListener(ev, function () { self.dragMode = null; });
      });
      this.el.cropStage.addEventListener('touchmove', function (e) { e.preventDefault(); }, { passive: false });

      this.el.cropCancel.addEventListener('click', function () { self.closeCrop(); });
      this.el.cropConfirm.addEventListener('click', function () { self.confirmCrop(); });

      /* Escape hatches: clicking the dark backdrop, or pressing Escape, must
         always be able to close the modal — even if the panel inside it fails
         to render for some reason, the backdrop itself still captures clicks
         (it's a full-viewport position:fixed layer), so without this a broken
         render permanently traps the shopper with no way to continue. */
      this.el.cropMask.addEventListener('click', function (e) {
        if (e.target === self.el.cropMask) self.closeCrop();
      });
      this._onCropKeydown = function (e) {
        if (e.key === 'Escape' && self.el.cropMask.classList.contains('show')) self.closeCrop();
      };
      document.addEventListener('keydown', this._onCropKeydown);

      this.el.detailsToggle.addEventListener('click', function () {
        var open = self.el.detailsBody.classList.toggle('open');
        self.el.detailsToggle.textContent = open ? '− Hide details' : '+ Add details';
      });

      this.form = this.querySelector('form');
      // Add to Cart is type="button", not type="submit" — deliberately, so we
      // control the whole sequence: fetch the real price first, swap in the
      // correctly-priced variant, THEN trigger the real form submission (which
      // the theme's own <product-form> handles exactly as it does everywhere
      // else — cart drawer, loading state, etc. — since by that point it's a
      // completely normal submit with the right variant id already in place).
      this.el.atcBtn.addEventListener('click', function () { self.handleAddToCart(); });
    }

    async handleAddToCart(){
      if (this.el.atcBtn.disabled) return;
      /* Clicking here fires focusout on whichever cw/ch field was still
         focused, but that field's own finalize is deliberately deferred (see
         the focusout listener in bindEvents) so it doesn't clamp while the
         customer's still mid-entry. The deferred check hasn't run yet at
         this point in the same synchronous click — so clamp state.w/h here
         too, synchronously, as the last gate before anything is priced or
         submitted. */
      this.setCustomSize(this.state.w, this.state.h);
      this.syncProperties();

      var self = this;
      var settled = false;
      var timer = null;
      /* Guarded and idempotent: the button used to reset ONLY on the theme's
         cart:change / cart:error events, so if neither ever fired the customer
         was left with a permanently dead "Adding to cart…" button and no way
         back. That's the "gets stuck" report. */
      var resetButton = function () {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        document.removeEventListener('cart:change', resetButton);
        document.removeEventListener('cart:error', resetButton);
        self.el.atcBtn.disabled = false;
        self.el.atcBtn.textContent = ATC_LABEL;
      };

      this.el.atcBtn.disabled = true;
      /* Uploading the artwork is the slow part — on mobile data a large file
         can take a while — so name what's actually happening rather than
         letting it look frozen. */
      var hasFile = this.el.fileInput.files && this.el.fileInput.files.length;
      this.el.atcBtn.textContent = hasFile ? 'Uploading artwork…' : 'Adding to cart…';
      this.el.atcNote.textContent = '';

      /* Last-resort release. Better a retry than a button that never comes back. */
      timer = setTimeout(function () {
        if (settled) return;
        resetButton();
        self.el.atcNote.textContent = 'That is taking longer than usual — check your cart before trying again, in case it went through.';
      }, 45000);

      try {
        /* If a crop rebuild is still in flight (e.g. the customer just
           undid "email it later" and clicked Add to Cart before the
           restored crop finished re-rendering into croppedInput), wait for
           it here. This is the one gate every path funnels through, so it
           closes the race regardless of which caller started the rebuild
           or whether that caller itself awaited it. */
        /* A quantity or design name changed after the crop was baked postdates
           the designer SVG's on-artboard label. Regenerate before submitting so
           the label matches the order. Reassigns _artworkReady, so the await
           below covers it. */
        if (this._svgStale) this.refreshCroppedUpload();

        if (this._artworkReady) await this._artworkReady;

        var response = await fetch('/apps/pricer/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            width: this.state.w,
            height: this.state.h,
            qty: this.state.qty,
            /* The variant attaches to THIS real product rather than a
               shared internal container, so checkout's bold title is the
               actual product ("Custom Mylar Stencils") natively — no
               workaround needed. Tradeoff, accepted deliberately: every
               generated variant now lives on the real product's own variant
               list in admin, not tucked away. Flagged by Colin, 2026-08-13. */
            productId: this.dataset.productId
          })
        });
        var result = await response.json();
        if (!response.ok || !result.variantId) {
          throw new Error(result.error || 'Could not get a price for this configuration.');
        }

        var idField = this.form.querySelector('input[name="id"]');
        idField.value = result.variantId.split('/').pop();

        this.el.atcBtn.textContent = hasFile ? 'Uploading artwork…' : 'Adding to cart…';
        /* The backend (quote.jsx) creates this exact variant via the Admin
           API moments before handing it back here. Confirmed live,
           2026-08-12 (Colin + John Paul via DevTools, then again after the
           backend added its own pre-return verification): Shopify's
           Storefront cart can still fail to find a variant that new with
           "Cannot find variant" even after the backend confirms the SAME
           variant is already readable back through the Admin API — Admin
           and Storefront visibility propagate on separate timelines, so
           Admin-side confirmation doesn't guarantee Storefront-side
           readiness. That means this retry loop, hitting the real
           Storefront endpoint, is the only place that can actually observe
           when it's safe to proceed — increased delays (exponential
           backoff, ~1s to ~16s, six attempts) to give real room for that
           gap, up from an initial 700ms that consistently wasn't enough.

           The theme's own cart:change / cart:error events (dispatched on
           the form, see theme.js) fire once each real /cart/add.js request
           resolves — that's what tells us whether to retry, succeed, or
           give up, since our button is no longer type="submit" and so isn't
           covered by the theme's own submit-button busy-state handling. */
        var retriesLeft = 6;
        var nextDelayMs = 1000;
        var cleanupCartListeners = function () {
          document.removeEventListener('cart:change', onAdded);
          self.form.removeEventListener('cart:error', onAddError);
        };
        var onAdded = function () {
          cleanupCartListeners();
          resetButton();
        };
        var onAddError = function (e) {
          cleanupCartListeners();
          if (settled) return; // the 45s safety timeout already gave up
          var message = e.detail && e.detail.error;
          if (message === 'Cannot find variant' && retriesLeft > 0) {
            retriesLeft -= 1;
            self.el.atcBtn.textContent = 'Finalizing your price…';
            setTimeout(submitToCart, nextDelayMs);
            nextDelayMs = Math.min(nextDelayMs * 2, 5000);
            return;
          }
          resetButton();
          self.el.atcNote.textContent = "Couldn't add that to your cart — please try again.";
        };
        /* requestSubmit() runs the browser's OWN native constraint
           validation across every field in the form before it actually
           submits — including cw/ch, which are real <input type="number">
           elements. Those never had a `step` attribute (see
           pricer-app.liquid), so number inputs default to step="1", making
           a decimal like 4.25 count as invalid. The browser then silently
           blocks the submit and shows its own validity bubble instead of
           firing anything — neither cart:change nor cart:error, so the
           button was left stuck on "Uploading artwork…" forever until the
           45s safety timeout. Caught live, 2026-08-12: fixed by adding
           step="any" to every size input, but documenting here too since
           it's exactly the kind of failure this function can't see or
           report on — it just silently never gets called. */
        var submitToCart = function () {
          document.addEventListener('cart:change', onAdded, { once: true });
          self.form.addEventListener('cart:error', onAddError, { once: true });
          self.form.requestSubmit();
        };
        submitToCart();
      } catch (err) {
        resetButton();
        this.el.atcNote.textContent = "Couldn't get a price for this configuration — please try again.";
        console.error('[pricer] quote fetch failed:', err);
      }
    }

    setQty(q){
      this.state.qtyTouched = true;
      var wasQty = this.state.qty;
      this.state.qty = Math.min(500, Math.max(1, q || 1));
      /* The designer SVG prints the quantity on the artboard but is generated
         when the crop is baked, which can be well before this. Mark it stale
         and let Add to Cart regenerate once — rebuilding per click would
         re-encode the whole image. Found on v2 as a live "qty 1" label on a
         qty-2 order (#56462). */
      if (this.state.qty !== wasQty) this._svgStale = true;
      this.el.qty.value = this.state.qty;
      this.render();
    }

    /*
      Clamps custom width/height to the pricing table's real range (2-48")
      and writes the clamped value back into the inputs, same pattern as
      setQty above. Called on preset-button clicks and on blur of the cw/ch
      fields — NOT on every keystroke (see the 'input' listener in
      bindEvents, which updates state live but leaves the fields alone while
      the customer is still typing). Without this, the HTML min/max
      attributes are purely advisory — they block native form submission but
      don't stop someone from typing e.g. 50000, which the price preview
      would silently clamp for its own math while leaving the input showing
      50000, and Add to Cart would then hard-fail once the backend's own
      validation rejects it. Flagged by Colin, 2026-08-09. NaN (blank field)
      is left as null, matching the "not yet entered" state the rest of the
      app expects — only clamps once there's an actual number.
    */
    setCustomSize(w, h){
      this.state.w = Number.isFinite(w) ? Math.min(MAX_DIM, Math.max(MIN_DIM, w)) : null;
      this.state.h = Number.isFinite(h) ? Math.min(MAX_DIM, Math.max(MIN_DIM, h)) : null;
      if (this.state.w !== null) this.el.cw.value = this.state.w;
      if (this.state.h !== null) this.el.ch.value = this.state.h;
    }

    render(){
      var u = quote(this.state.w, this.state.h, this.state.qty);
      var artworkChosen = this.state.artUploaded || this.state.sendLater;
      /* Right at the upload button, not just buried in atcNote down by the
         price bar — an oversized file used to only show its rejection
         there, easy to miss entirely, which read as "nothing happens" when
         picking a file too large. Set before the early-return below so it's
         current even if no size has been picked yet. Flagged by Colin,
         2026-08-13. */
      this.el.uploadErrorNote.textContent = this.state.uploadError || '';
      if (this.el.oversizeNote) this.el.oversizeNote.classList.toggle('show', !!this.state.sizeExceedsMax);

      /* Step markers stay plain numbers. The green-tick flow made a perfectly
         valid order look half-finished: quantity defaults to 1 and details are
         optional, so those two never lit up unless you touched them. */

      if (!u) {
        this.el.sizeNote.textContent = '';
        this.el.total.textContent = '—'; this.el.perUnit.textContent = ''; this.el.saveLine.textContent = '';
        this.el.atcBtn.disabled = true;
        this.el.atcNote.textContent = 'Pick a size to see your price';
        this.renderSavings(null);
        return;
      }

      var singlesTotal = u.single * this.state.qty;
      var save = singlesTotal - u.total;
      this.el.total.textContent = money(u.total);
      this.el.perUnit.textContent = money(u.unit) + ' each · ' + this.state.w + '×' + this.state.h + '″ × ' + this.state.qty;
      this.el.saveLine.textContent = (this.state.qty > 1 && save > 0.005)
        ? 'You save ' + money(save) + ' (' + Math.round(save / singlesTotal * 100) + '% off)'
        : '';

      var next = quote(this.state.w, this.state.h, this.state.qty + 1);
      var nextPct = u.single ? Math.round((1 - next.unit / u.single) * 100) : 0;
      /* A percentage travels better than a per-unit price — on mobile the total
         isn't visible next to this, so the saving is the useful number. */
      this.el.tierNudge.textContent = this.state.qty >= 500
        ? 'Best price unlocked'
        : nextPct > 0 ? 'Add 1 more → save ' + nextPct + '%' : 'Add 1 more for a better price';

      this.el.sizeNote.textContent = 'Artwork: ' + this.state.w + ' × ' + this.state.h + '″ · Full sheet: ' + (this.state.w + 2) + ' × ' + (this.state.h + 2) + '″ (1″ border each side).';

      /* A file we can't rasterise can't be proportion-checked, so it would
         otherwise sail through the exact gate everything else has to pass.
         Require an explicit acknowledgement instead of silently allowing it. */
      var needsAck = this.state.artUploaded && !this.state.previewable;
      var acked = !needsAck || (this.el.noPreviewAck && this.el.noPreviewAck.checked);
      if (needsAck) this.el.noPreviewSize.textContent = this.state.w + ' × ' + this.state.h + '″';

      /* The gate. An unresolved shape mismatch stops the order outright — the
         customer has to crop, change size, or knowingly accept the stretch.
         Nothing gets through undecided. */
      var unresolved = false; // stretching is the default outcome, so nothing is left undecided

      this.el.atcBtn.disabled = !artworkChosen || !acked || unresolved;
      this.el.atcNote.textContent = !artworkChosen
        ? (this.state.uploadError || "Choose how you'll send your artwork to continue")
        : unresolved ? 'Your artwork isn’t this shape — choose how to handle it above'
        : !acked ? 'Confirm the note above so we know how to proof your file'
        : '';
      this.renderSavings(u);
    }

    renderSavings(u){
      var el = this.el.savingsTable;
      el.innerHTML = '';
      if (!u) { el.innerHTML = '<div class="row"><span>Pick a size first</span></div>'; return; }
      var marks = [1, 2, 3, 5, 10, 25];
      var cur = marks.filter(function (m) { return m <= this.state.qty; }, this).pop() || 1;
      var self = this;
      marks.forEach(function (m) {
        var qq = quote(self.state.w, self.state.h, m);
        var pct = Math.round((1 - qq.unit / u.single) * 100);
        var row = document.createElement('div');
        row.className = 'row' + (m === cur ? ' active' : '');
        row.innerHTML = '<span>Qty ' + m + '</span><span>' + money(qq.unit) + ' each' +
          (pct > 0 ? ' <span class="save">save ' + pct + '%</span>' : '') + '</span>';
        el.appendChild(row);
      });
    }

    /* Renders exactly what the customer selected — the crop applied to the
       artwork — and uploads it alongside the original file. Crop coordinates on
       their own are unusable by a designer; this is the selection as a picture.
       Also gives non-image uploads (PDF, AI) a real thumbnail, since the raster
       we already produced for the preview is a PNG.

       Returns a Promise that resolves once croppedInput.files is actually
       set (or once there's nothing to do) — this used to be fire-and-forget,
       which left a real gap: confirmCrop() closed the dialog immediately,
       and a customer who confirmed their crop and clicked Add to Cart
       quickly could submit before this async chain (image decode -> canvas
       draw -> toBlob callback) finished, silently missing the cropped file.
       Confirmed as a genuine risk during testing, 2026-08-13, though not
       reproduced as the direct cause of Colin's report — closing it anyway
       since the failure mode fits ("not coming through", intermittent). */
    /* ------------------------------------------------------------------
       DESIGNER SVG — ported from pricer-v2.js, 2026-08-21, so Custom Mylar
       gets it too. Colin scoped it to "this new version"; Custom Mylar is
       the product actually taking orders, so the designers only feel the
       benefit here.

       One file, two Illustrator layers: the artwork, and a crop frame the
       designer hides or deletes. The page is declared in INCHES so the crop
       measures the ordered size on the artboard.

       WHAT'S DIFFERENT FROM v2: this version still has a stretch path. When
       the artwork was stretched to fit rather than cropped, there is no crop
       region — the whole image becomes the stencil, at a non-uniform scale.
       An SVG expresses that exactly: declare the page as the ordered size and
       let the viewBox stretch the image into it. Handled in buildDesignerSvg.
    ------------------------------------------------------------------ */

    svgArtworkBudget(){
      var spent = this._fileBytes || 0;
      return Math.max(3 * 1024 * 1024, Math.min(SVG_ARTWORK_BUDGET, 18 * 1024 * 1024 - spent));
    }

    /* Embeds state.fullSrc — the canvas re-encode, not the customer's original
       bytes. Deliberate: a phone photo carries EXIF rotation, canvas decoding
       applies it and SVG <image> does not, so embedding the raw file would open
       sideways with the frame over the wrong region. savedCrop's fractions are
       measured against the oriented image, so this keeps both in one space. */
    encodeArtworkForSvg(im){
      var budget = this.svgArtworkBudget();
      if (this.state.fullSrc && this.state.fullSrc.length <= budget) return this.state.fullSrc;
      var natW = im.naturalWidth, natH = im.naturalHeight;
      var scale = 1;
      for (var i = 0; i < 5; i++) {
        var cv = document.createElement('canvas');
        cv.width = Math.max(1, Math.round(natW * scale));
        cv.height = Math.max(1, Math.round(natH * scale));
        var ctx = cv.getContext('2d');
        /* JPEG has no alpha — without this a transparent PNG bakes to black. */
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, cv.width, cv.height);
        ctx.drawImage(im, 0, 0, cv.width, cv.height);
        var href;
        try { href = cv.toDataURL('image/jpeg', 0.92); } catch (e) { return null; }
        if (href.length <= budget) return href;
        scale *= 0.7;
      }
      return null;
    }

    buildDesignerSvg(im){
      var natW = im.naturalWidth, natH = im.naturalHeight;
      if (!natW || !natH || !this.state.w || !this.state.h) return null;

      var href = this.encodeArtworkForSvg(im);
      if (!href) return null;

      /* Stretched orders have no crop region: the whole image is the stencil.
         Treat it as a full-image "crop", which makes the frame the page edge
         and the inch maths below collapse to the ordered size exactly. */
      var stretched = this.state.fitMode === 'stretch' || !this.state.savedCrop;
      var s = stretched ? { x: 0, y: 0, w: 1, h: 1 } : this.state.savedCrop;

      var cx = s.x * natW, cy = s.y * natH;
      var cw = s.w * natW, ch = s.h * natH;

      var inW = this.state.w / s.w;
      var inH = this.state.h / s.h;

      /* A small crop of a big image implies a huge page — 8% of the width on a
         48" order is a 600-inch artboard, past Illustrator's 227.54" ceiling,
         and the file won't open. Scale down uniformly and say so on the
         artboard: a file that opens with its scale stated beats one that
         doesn't open. */
      var MAX_ARTBOARD_IN = 220;
      var over = Math.max(inW, inH) / MAX_ARTBOARD_IN;
      var scaleNote = '';
      if (over > 1) {
        inW /= over;
        inH /= over;
        scaleNote = ' · NOT AT SIZE — shown at 1:' + (Math.round(over * 1000) / 1000) +
          ', cut to the stated size above';
      }
      inW = Math.round(inW * 10000) / 10000;
      inH = Math.round(inH * 10000) / 10000;

      var sw = Math.max(2, natW * 0.004);
      var fs = Math.max(14, natW * 0.02);
      var labelY = (cy - sw * 2 >= fs * 1.2) ? (cy - sw * 2) : (cy + fs * 1.4);

      var dname = (this.el.dname && this.el.dname.value || '').trim();
      var label = this.state.w + '" × ' + this.state.h + '"' +
        ' · qty ' + this.state.qty +
        (dname ? ' · ' + dname : '') +
        (stretched ? ' · STRETCHED TO FIT' : '') + scaleNote;
      /* Text-node escaping only — & and < are the sole characters that can
         break a text node, and escaping quotes would print the inch marks as
         &quot; in a file a human opens and reads. */
      var labelXml = String(label).replace(/&/g, '&amp;').replace(/</g, '&lt;');

      return '<?xml version="1.0" encoding="UTF-8"?>\n' +
        '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"\n' +
        '     width="' + inW + 'in" height="' + inH + 'in" viewBox="0 0 ' + natW + ' ' + natH + '">\n' +
        '  <g id="artwork">\n' +
        '    <image xlink:href="' + href + '" x="0" y="0" width="' + natW + '" height="' + natH + '"/>\n' +
        '  </g>\n' +
        '  <g id="CROP-FRAME--DO-NOT-PRINT">\n' +
        '    <path d="M0,0 H' + natW + ' V' + natH + ' H0 Z M' + cx + ',' + cy +
                 ' V' + (cy + ch) + ' H' + (cx + cw) + ' V' + cy + ' Z"\n' +
        '          fill="#141414" fill-opacity="0.45" fill-rule="evenodd"/>\n' +
        '    <rect x="' + cx + '" y="' + cy + '" width="' + cw + '" height="' + ch + '"\n' +
        '          fill="none" stroke="#FFDE1B" stroke-width="' + sw + '"/>\n' +
        '    <text x="' + (cx + sw * 2) + '" y="' + labelY + '"\n' +
        '          font-family="Arial, Helvetica, sans-serif" font-size="' + fs + '" font-weight="bold"\n' +
        '          fill="#FFDE1B" stroke="#141414" stroke-width="' + (fs * 0.06) + '" paint-order="stroke">' +
                 labelXml + '</text>\n' +
        '  </g>\n' +
        '</svg>\n';
    }

    attachDesignerSvg(im){
      if (!this.el.designerInput) return;
      var svg;
      try { svg = this.buildDesignerSvg(im); } catch (e) { svg = null; }
      if (!svg) { this.el.designerInput.value = ''; return; }
      try {
        var raw = (this.el.dname && this.el.dname.value) || 'stencil-order';
        var name = raw.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'stencil-order';
        var dt = new DataTransfer();
        dt.items.add(new File([svg], name + '-designer.svg', { type: 'image/svg+xml' }));
        this.el.designerInput.files = dt.files;
      } catch (e) {
        /* Older browsers can't build a FileList. The original and the cropped
           preview still go through — this file is an addition, never a
           replacement, so losing it costs convenience, not data. */
        this.el.designerInput.value = '';
      }
    }

    refreshCroppedUpload(){
      var self = this;
      if (!this.state.fullSrc || !this.el.croppedInput) return Promise.resolve();
      var promise = new Promise(function (resolve) {
        var im = new Image();
        im.onload = function () {
          var s = self.state.savedCrop || { x: 0, y: 0, w: 1, h: 1 };
          var sw = Math.max(1, Math.round(s.w * im.naturalWidth));
          var sh = Math.max(1, Math.round(s.h * im.naturalHeight));
          /* Cap the long edge — a full-resolution crop of a large scan would
             balloon the upload for no benefit; the original carries the detail. */
          var scale = Math.min(1, 2000 / Math.max(sw, sh));
          var cv = document.createElement('canvas');
          cv.width = Math.max(1, Math.round(sw * scale));
          cv.height = Math.max(1, Math.round(sh * scale));
          cv.getContext('2d').drawImage(
            im, s.x * im.naturalWidth, s.y * im.naturalHeight, sw, sh, 0, 0, cv.width, cv.height
          );
          self.el.thumb.src = cv.toDataURL('image/png');

          /* Built from the same loaded image and the same crop fractions, in
             the same pass, so the frame can never describe a different crop
             than the preview does. Inside this promise deliberately —
             _artworkReady then gates Add to Cart on the SVG too. */
          self.attachDesignerSvg(im);
          self._svgStale = false;

          if (!cv.toBlob) { resolve(); return; }
          cv.toBlob(function (blob) {
            if (!blob) { resolve(); return; }
            try {
              var dt = new DataTransfer();
              dt.items.add(new File([blob], 'cropped-artwork.png', { type: 'image/png' }));
              self.el.croppedInput.files = dt.files;
            } catch (e) {
              /* Older browsers can't build a File list programmatically. The
                 original file and the crop coordinates still go through. */
            }
            resolve();
          }, 'image/png');
        };
        im.onerror = function () { resolve(); }; // don't hang the caller on a bad fullSrc
        im.src = self.state.fullSrc;
      });
      /* Tracked here, not just returned, because callers vary in whether they
         await this — toggleSendLater's restore path doesn't block on it (no
         modal holds the customer back the way the crop dialog does), so
         Add to Cart is left as the one place that MUST wait, regardless of
         which caller kicked off the rebuild or whether that caller awaited
         it. Closes the race structurally instead of per-call-site. */
      this._artworkReady = promise;
      return promise;
    }

    /* A shape mismatch no longer interrupts the customer with a choice. It goes
       straight to a standing note saying the design will be stretched — which is
       what happens if they do nothing — and leaves cropping available for anyone
       who wants it. The order still records which of the two occurred. */
    checkAspect(){
      var flag = this.el.aspectFlag;
      var artRatio = this.state.cropRatio || (this.state.artW && this.state.artH ? this.state.artW / this.state.artH : null);
      var mismatch = false;
      if (artRatio && this.state.w && this.state.h) {
        mismatch = !fitsSize(artRatio, this.state.w, this.state.h);
      }
      this.state.mismatch = mismatch;

      if (!mismatch) {
        /* A stretch decision only applies to the mismatch that prompted it —
           once the shapes agree, it's stale and must not linger on the order. */
        if (this.state.fitMode === 'stretch') this.state.fitMode = null;
        flag.classList.remove('show');
        this.el.aspectAck.classList.remove('show');
        return;
      }

      /* Stretching is the default outcome, so say so rather than demanding a
         decision first. */
      if (!this.state.fitMode) this.state.fitMode = 'stretch';

      /* Concrete inches beat ratios here. "Your artwork is 1:1 but your
         stencil is 24:24" was the old copy — both sides are the same ratio
         once reduced, so a real 15%-off mismatch read as a bug. */
      var naturalH = snap4(this.state.w / artRatio);
      var chosen = this.state.w + ' × ' + this.state.h + '″';
      this.el.artRatio.textContent = this.state.w + '″';
      this.el.sheetRatio.textContent = naturalH + '″';
      this.el.chosenSize.textContent = chosen;
      this.el.aspectKeep.textContent = 'Stretch to ' + chosen;
      this.el.aspectMatch.textContent = 'Crop artwork to ' + chosen;
      this.el.ackSize.textContent = chosen;
      this.el.ackNatural.textContent = this.state.w + ' × ' + naturalH + '″';
      this.el.aspectUndo.textContent = 'Crop it to ' + chosen + ' instead';

      /* The two-button decision screen is gone; the note is the only thing a
         mismatch produces now. The flag element stays in the markup so the
         choice can be restored without a rebuild. */
      flag.classList.remove('show');
      this.el.aspectAck.classList.add('show');
    }

    useSampleArt(){
      var cv = document.createElement('canvas'); cv.width = 1200; cv.height = 1000;
      var cx = cv.getContext('2d');
      cx.fillStyle = '#FFFFFF'; cx.fillRect(0, 0, 1200, 1000);
      cx.fillStyle = '#141414'; cx.beginPath(); cx.arc(600, 430, 300, 0, Math.PI * 2); cx.fill();
      cx.fillStyle = '#FFDE1B'; cx.beginPath(); cx.arc(600, 430, 190, 0, Math.PI * 2); cx.fill();
      cx.fillStyle = '#141414'; cx.font = '900 130px sans-serif'; cx.textAlign = 'center';
      cx.fillText('SS', 600, 478);
      cx.font = '700 64px sans-serif'; cx.fillText('SAMPLE ART', 600, 900);
      var url = cv.toDataURL('image/png');

      this.state.artUploaded = true; this.state.sendLater = false; this.state.cropRatio = null; this.state.savedCrop = null; this.state.fitMode = null; this.state.previewable = false;
      /* Clear the OLD file's measurements too — leaving them meant the shape
         check ran against the previous artwork until the new one finished
         loading, so the warning reflected a file that was already gone. */
      this.state.artW = null; this.state.artH = null; this.state.fullSrc = null; this.state.mismatch = false; if (this.el.croppedInput) this.el.croppedInput.value = ''; if (this.el.designerInput) this.el.designerInput.value = '';
      this.state.artW = 1200; this.state.artH = 1000; this.state.fullSrc = url; this.state.previewable = true;
      this.el.laterLink.textContent = 'Still working on your file? Email it after ordering';
      this.el.laterLink.classList.remove('active'); this.el.laterNote.classList.remove('show');
      this.el.fname.textContent = 'sample-design.png';
      if (!this.el.dname.value) this.el.dname.value = 'sample-design';
      this.el.uploadZone.classList.add('hidden'); this.el.noPreviewNote.classList.remove('show');
      this.el.uploadSuccess.classList.add('show');
      this.el.thumb.src = url;
      this.el.upStatus.textContent = '✓ Upload complete';
      this.checkAspect(); this.render(); this.openCrop();
    }

    /* Changing the ordered size invalidates any earlier decision about how the
       artwork fits it — a crop made for 24 × 24″ says nothing about 24 × 10″.
       Without this, switching size after resolving one mismatch let the next one
       through unchallenged. */
    sizeChanged(){
      this.state.fitMode = null;
      this.checkAspect();
      this.render();
    }

    async toggleSendLater(){
      this.state.sendLater = !this.state.sendLater;
      if (this.state.sendLater) {
        /* No file means nothing to compare against — drop the measurements so a
           mismatch from a previous upload can't keep blocking checkout. But if
           there WAS already an upload, this used to discard it outright —
           clicking "undo" a moment later brought back an empty upload zone,
           not the file the customer had already chosen, since there was
           nothing left to restore. Snapshot it here instead, so undo (the
           else branch below) can put it back exactly as it was. Flagged by
           Colin, 2026-08-12. The real file in el.fileInput is untouched by
           any of this — only the derived measurements/crop and the visible
           "uploaded" UI ever got cleared, which is what's being preserved
           and restored now. */
        if (this.state.artUploaded) {
          this.state.pausedUpload = {
            artW: this.state.artW, artH: this.state.artH,
            cropRatio: this.state.cropRatio, savedCrop: this.state.savedCrop,
            fitMode: this.state.fitMode, previewable: this.state.previewable,
            fullSrc: this.state.fullSrc, mismatch: this.state.mismatch,
            thumbSrc: this.el.thumb.src, fname: this.el.fname.textContent,
            upStatus: this.el.upStatus.textContent,
            noPreviewShown: this.el.noPreviewNote.classList.contains('show')
          };
        }
        this.state.artUploaded = false; this.el.uploadSuccess.classList.remove('show');
        this.state.artW = null; this.state.artH = null;
        this.state.cropRatio = null; this.state.savedCrop = null;
        this.state.fitMode = null; this.state.previewable = false; if (this.el.croppedInput) this.el.croppedInput.value = ''; if (this.el.designerInput) this.el.designerInput.value = '';
        this.checkAspect();
        this.el.noPreviewNote.classList.remove('show');
        if (this.el.noPreviewAck) this.el.noPreviewAck.checked = false;
        this.el.uploadZone.classList.remove('hidden');
        this.el.laterLink.textContent = "✓ You'll email artwork after ordering — undo";
      } else {
        var paused = this.state.pausedUpload;
        if (paused) {
          this.state.artUploaded = true;
          this.state.artW = paused.artW; this.state.artH = paused.artH;
          this.state.cropRatio = paused.cropRatio; this.state.savedCrop = paused.savedCrop;
          this.state.fitMode = paused.fitMode; this.state.previewable = paused.previewable;
          this.state.fullSrc = paused.fullSrc; this.state.mismatch = paused.mismatch;
          this.el.thumb.src = paused.thumbSrc;
          this.el.fname.textContent = paused.fname;
          this.el.upStatus.textContent = paused.upStatus;
          if (paused.noPreviewShown) this.el.noPreviewNote.classList.add('show');
          this.el.uploadZone.classList.add('hidden');
          this.el.uploadSuccess.classList.add('show');
          this.state.pausedUpload = null;
          await this.refreshCroppedUpload(); // rebuilds the cropped-render file from the restored fullSrc/savedCrop
          this.checkAspect();
        }
        this.el.laterLink.textContent = 'Still working on your file? Email it after ordering';
      }
      this.el.laterLink.classList.toggle('active', this.state.sendLater);
      this.el.laterNote.classList.toggle('show', this.state.sendLater);
      this.render();
    }

    onFileChosen(f){
      if (!f) return;
      var self = this;

      /* Kept so the designer SVG can size its embedded copy against what the
         original is already costing this request — see svgArtworkBudget. */
      this._fileBytes = f.size;

      /* Shopify rejects line-item property files over 20MB. Catch it here with a
         clear message rather than letting Add to Cart fail at the last step,
         after the customer has configured everything. */
      if (f.size > MAX_UPLOAD_BYTES) {
        this.el.fileInput.value = '';
        this.el.uploadZone.classList.remove('hidden');
        this.el.uploadSuccess.classList.remove('show');
        this.el.noPreviewNote.classList.remove('show');
        this.state.artUploaded = false; this.state.previewable = false;
        /* Held in state, not written straight to the DOM — render() rewrites
           that node, which was silently swallowing this message and making an
           oversized file look like it simply hadn't uploaded. */
        this.state.uploadError = 'That file is ' + (f.size / 1048576).toFixed(0) +
          'MB. Files over 20MB can’t be attached to an order — send a smaller version, or choose "Email it after ordering" below.';
        this.render();
        return;
      }
      this.state.uploadError = null;
      this.state.artUploaded = true; this.state.sendLater = false; this.state.cropRatio = null; this.state.savedCrop = null; this.state.fitMode = null; this.state.previewable = false;
      /* Clear the OLD file's measurements too — leaving them meant the shape
         check ran against the previous artwork until the new one finished
         loading, so the warning reflected a file that was already gone. */
      this.state.artW = null; this.state.artH = null; this.state.fullSrc = null; this.state.mismatch = false; if (this.el.croppedInput) this.el.croppedInput.value = ''; if (this.el.designerInput) this.el.designerInput.value = '';
      this.el.laterLink.textContent = 'Still working on your file? Email it after ordering';
      this.el.laterLink.classList.remove('active'); this.el.laterNote.classList.remove('show');
      this.el.fname.textContent = f.name;
      if (!this.el.dname.value) this.el.dname.value = f.name.replace(/\.[^.]+$/, '');
      this.el.uploadZone.classList.add('hidden');
      this.el.noPreviewNote.classList.remove('show');
      this.el.uploadSuccess.classList.add('show');

      if (f.type.indexOf('image/') === 0) {
        var fr = new FileReader();
        fr.onload = function () {
          self.el.thumb.src = fr.result;
          var img = new Image();
          img.onload = function () {
            self.state.artW = img.naturalWidth; self.state.artH = img.naturalHeight;
            self.state.fullSrc = fr.result; self.state.previewable = true;
            var ar = self.state.artW / self.state.artH;
            var matches = fitsSize(ar, self.state.w, self.state.h);
            self.checkAspect(); self.render();
            self.refreshCroppedUpload();
            /* Crop now opens on every upload, matched or not — the customer
               gets a chance to fine-tune the selection regardless, rather
               than only seeing it when their artwork's proportions happen
               not to match. Decided by Colin, 2026-08-12. */
            self.el.upStatus.textContent = matches
              ? '✓ Upload complete — fits your ' + self.state.w + ' × ' + self.state.h + '″'
              : '✓ Upload complete';
            self.openCrop();
          };
          img.onerror = function () {
            self.state.artW = null; self.state.artH = null; self.el.thumb.src = FILE_ICON;
            self.el.noPreviewNote.classList.add('show'); self.checkAspect(); self.render();
          };
          img.src = fr.result;
        };
        fr.onerror = function () {
          self.state.artW = null; self.state.artH = null; self.el.thumb.src = FILE_ICON;
          self.el.noPreviewNote.classList.add('show'); self.render();
        };
        fr.readAsDataURL(f);
      } else if (isPdfLike(f)) {
        /* PDF (and most .ai files, which carry a PDF stream when saved with
           "Create PDF Compatible File") render via PDF.js. Without this these
           files skipped the proportion check entirely — which is exactly the
           class of order that causes production problems. */
        this.el.upStatus.textContent = 'Reading your file…';
        this.renderPdfPreview(f);
      } else {
        this.el.thumb.src = FILE_ICON; this.state.artW = null; this.state.artH = null;
        this.state.previewable = false;
        this.el.noPreviewNote.classList.add('show');
      }
      this.checkAspect(); this.render();
    }

    /* Optional extra files (a second reference photo, notes, a font file) —
       deliberately separate from onFileChosen above. These never touch
       state.artW/artH/fullSrc or the crop/proportion flow; they're attached
       for the design team exactly as picked, same as the primary file is
       before any cropping happens to it. Accumulates across multiple picks
       (so "add one now, one more later" works) rather than replacing the
       set each time, capped at MAX_REF_FILES. */
    onRefFilesChosen(fileList){
      var self = this;
      var incoming = Array.prototype.slice.call(fileList);
      var room = MAX_REF_FILES - this.state.refFiles.length;
      var overflow = incoming.length > room;
      this.state.refFiles = this.state.refFiles.concat(incoming.slice(0, Math.max(0, room)));
      this.syncRefFileSlots();
      this.renderRefFiles();
      if (overflow) {
        this.el.refFilesBtn.textContent = 'Only ' + MAX_REF_FILES + ' reference files max — the rest were skipped';
        setTimeout(function () {
          self.el.refFilesBtn.textContent = self.state.refFiles.length >= MAX_REF_FILES
            ? 'Reference file limit reached'
            : '+ Add reference files (optional)';
        }, 3500);
      }
    }

    /* Writes state.refFiles into the fixed properties[_Reference File 1..N]
       inputs so they're what actually gets submitted — this is the one
       place that touches those inputs, so state.refFiles stays the single
       source of truth regardless of whether a file arrived via a fresh pick
       or survived a removal. */
    syncRefFileSlots(){
      var self = this;
      for (var i = 1; i <= MAX_REF_FILES; i++) {
        var slot = this.$('refFileSlot' + i);
        if (!slot) continue;
        var file = self.state.refFiles[i - 1];
        if (file) {
          try {
            var dt = new DataTransfer();
            dt.items.add(file);
            slot.files = dt.files;
          } catch (e) { /* older browsers: this file just won't attach */ }
        } else {
          slot.value = '';
        }
      }
    }

    renderRefFiles(){
      var self = this;
      var list = this.el.refFilesList;
      if (!list) return;
      list.textContent = '';
      this.state.refFiles.forEach(function (file, idx) {
        var li = document.createElement('li');
        var name = document.createElement('span');
        name.textContent = file.name;
        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'ref-file-remove';
        remove.setAttribute('aria-label', 'Remove ' + file.name);
        remove.textContent = '×';
        remove.addEventListener('click', function () {
          self.state.refFiles.splice(idx, 1);
          self.syncRefFileSlots();
          self.renderRefFiles();
          self.el.refFilesBtn.textContent = '+ Add reference files (optional)';
        });
        li.appendChild(name); li.appendChild(remove);
        list.appendChild(li);
      });
      list.classList.toggle('show', this.state.refFiles.length > 0);
      if (this.state.refFiles.length >= MAX_REF_FILES) {
        this.el.refFilesBtn.textContent = 'Reference file limit reached';
      } else if (this.el.refFilesBtn.textContent === 'Reference file limit reached') {
        this.el.refFilesBtn.textContent = '+ Add reference files (optional)';
      }
    }

    /* Rasterises page 1 so the artwork can be measured and cropped like any
       image. PDF.js is ~370KB so it is only fetched once a PDF is actually
       chosen, never on page load. */
    async renderPdfPreview(f){
      var self = this;
      try {
        var lib = await loadPdfJs(this.dataset.pdfjs, this.dataset.pdfjsWorker);
        var buf = await f.arrayBuffer();
        var doc = await lib.getDocument({ data: buf }).promise;
        var page = await doc.getPage(1);
        var base = page.getViewport({ scale: 1 });
        /* Cap the raster so a large-format PDF doesn't blow up memory, but keep
           it detailed enough to crop against. */
        var scale = Math.min(2, 1600 / Math.max(base.width, base.height));
        var vp = page.getViewport({ scale: scale });
        var cv = document.createElement('canvas');
        cv.width = Math.max(1, Math.round(vp.width));
        cv.height = Math.max(1, Math.round(vp.height));
        await page.render({ canvasContext: cv.getContext('2d'), viewport: vp }).promise;
        var url = cv.toDataURL('image/png');

        self.state.artW = cv.width; self.state.artH = cv.height;
        self.state.fullSrc = url; self.state.previewable = true;
        self.el.thumb.src = url;
        self.el.noPreviewNote.classList.remove('show');

        var ar = cv.width / cv.height;
        var matches = fitsSize(ar, self.state.w, self.state.h);
        self.checkAspect(); self.render();
        self.el.upStatus.textContent = matches
          ? '✓ Upload complete — fits your ' + self.state.w + ' × ' + self.state.h + '″'
          : '✓ Upload complete';
        /* Free transform on first upload — the customer may well want a size
           driven by their artwork, and locking here felt like the tool fighting
           them. The locked mode is reserved for "Crop artwork to W × H", where
           keeping the chosen size is the whole point. Awaited since this
           function is already async and openCrop() right after is what makes
           the dialog modal — no reason to leave this one racing too. */
        await self.refreshCroppedUpload();
        // Always opens now, matched or not — see the matching image-upload path above.
        self.openCrop();
      } catch (err) {
        /* Encrypted, corrupt, or a non-PDF-compatible .ai — fall back to the
           un-previewable path rather than leaving the customer stuck. */
        self.state.artW = null; self.state.artH = null; self.state.previewable = false;
        self.el.thumb.src = FILE_ICON;
        self.el.noPreviewNote.classList.add('show');
        self.el.upStatus.textContent = '✓ Upload complete';
        self.checkAspect(); self.render();
      }
    }

    /* The crop is always free transform — the client's instruction was
       "proportions should never be locked". Drag any shape you like; the
       ordered size follows from what you selected, and the dialog shows those
       dimensions before you confirm, so nothing changes behind your back. */
    openCrop(){
      if (!this.state.artW) return;
      var self = this;
      var ci = this.el.cropImg;
      var src = this.state.fullSrc || this.el.thumb.src;
      document.body.classList.add('pricer-lock');
      this.el.cropMask.classList.add('show');
      this.el.cropHint.textContent = 'Set one dimension — we calculate the other so your stencil always matches your art. Drag the corners to use just part of your design.';

      var init = function () {
        requestAnimationFrame(function () {
          var r = ci.getBoundingClientRect();
          if (!r.width) { setTimeout(init, 60); return; }
          if (self.state.savedCrop) {
            var s = self.state.savedCrop;
            self.crop.x = s.x * r.width; self.crop.y = s.y * r.height; self.crop.w = s.w * r.width; self.crop.h = s.h * r.height;
          } else {
            self.crop.x = 0; self.crop.y = 0; self.crop.w = r.width; self.crop.h = r.height;
          }
          self.anchor = 'w';
          self.el.dimW.value = self.state.w || 12;
          self.el.cropCancel.textContent = (self.state.w && self.state.h) ? ('Keep ' + self.state.w + ' × ' + self.state.h + '″') : 'Enter size manually';
          self.layoutCrop(); self.updateComputed();
        });
      };
      if (ci.src === src && ci.complete && ci.naturalWidth) { init(); }
      else { ci.onload = init; ci.src = src; }
    }
    closeCrop(){ this.el.cropMask.classList.remove('show'); document.body.classList.remove('pricer-lock'); }
    layoutCrop(){
      var b = this.el.cropBox;
      b.style.left = this.crop.x + 'px'; b.style.top = this.crop.y + 'px';
      b.style.width = this.crop.w + 'px'; b.style.height = this.crop.h + 'px';
    }
    updateComputed(){
      if (!this.crop.w || !this.crop.h) return;
      var ratio = this.crop.w / this.crop.h;
      if (this.anchor === 'w') {
        var a = parseFloat(this.el.dimW.value);
        this.el.dimH.value = a ? snap4(a / ratio) : '';
      } else {
        var b = parseFloat(this.el.dimH.value);
        this.el.dimW.value = b ? snap4(b * ratio) : '';
      }
      this.refreshCropButtons();
    }
    refreshCropButtons(){
      var w = parseFloat(this.el.dimW.value), h = parseFloat(this.el.dimH.value);
      if (!w || !h) { this.el.cropConfirm.textContent = 'Use this size'; this.el.cropCancel.style.display = ''; return; }
      var dimsDirty = !(this.state.w === w && this.state.h === h);
      /* Cancel is a discard button labelled with what discarding keeps
         ("Keep W×H"); Confirm is what actually saves. Branching on
         cropDirty here — even when the resulting size still matched the
         one already committed — showed both at once: "Keep 7×8″" next to
         "Use new crop", two buttons implying two different outcomes for
         what's actually the same W×H either way. dimsDirty alone is what
         the customer can see and cares about; if the size didn't change,
         there's nothing to weigh against keeping it, crop position moved
         or not. Flagged by Colin, 2026-08-12. */
      if (dimsDirty) {
        this.el.cropConfirm.textContent = 'Use ' + w + ' × ' + h + '″';
        this.el.cropCancel.style.display = '';
      } else {
        this.el.cropConfirm.textContent = 'Keep ' + w + ' × ' + h + '″';
        this.el.cropCancel.style.display = 'none';
      }
    }

    onCropPointerDown(e){
      if (e.target.dataset.h) { this.dragMode = e.target.dataset.h; }
      else if (e.target === this.el.cropBox) { this.dragMode = 'move'; }
      else return;
      var r = this.el.cropImg.getBoundingClientRect();
      this.dragStart = { px: e.clientX - r.left, py: e.clientY - r.top, x: this.crop.x, y: this.crop.y, w: this.crop.w, h: this.crop.h };
      this.el.cropStage.setPointerCapture(e.pointerId);
      e.preventDefault();
    }
    onCropPointerMove(e){
      if (!this.dragMode) return;
      var r = this.el.cropImg.getBoundingClientRect();
      var px = Math.max(0, Math.min(r.width, e.clientX - r.left));
      var py = Math.max(0, Math.min(r.height, e.clientY - r.top));
      if (this.dragMode === 'move') {
        this.crop.x = Math.max(0, Math.min(r.width - this.dragStart.w, this.dragStart.x + (px - this.dragStart.px)));
        this.crop.y = Math.max(0, Math.min(r.height - this.dragStart.h, this.dragStart.y + (py - this.dragStart.py)));
      } else {
        var x1 = this.dragStart.x, y1 = this.dragStart.y, x2 = this.dragStart.x + this.dragStart.w, y2 = this.dragStart.y + this.dragStart.h;
        if (this.dragMode.indexOf('l') !== -1) x1 = Math.min(px, x2 - 30);
        if (this.dragMode.indexOf('r') !== -1) x2 = Math.max(px, x1 + 30);
        if (this.dragMode.indexOf('t') !== -1) y1 = Math.min(py, y2 - 30);
        if (this.dragMode.indexOf('b') !== -1) y2 = Math.max(py, y1 + 30);
        var nw = x2 - x1, nh = y2 - y1;
        this.crop.x = x1; this.crop.y = y1; this.crop.w = nw; this.crop.h = nh;
      }
      this.layoutCrop(); this.updateComputed();
    }

    async confirmCrop(){
      var self = this;
      var w = parseFloat(this.el.dimW.value), h = parseFloat(this.el.dimH.value);
      if (!w || !h) return;
      // Same gap as setCustomSize above, different entry point (crop modal's
      // own fields rather than the main custom-size fields) — clamp before
      // it reaches state.w/h, not after.
      w = Math.min(MAX_DIM, Math.max(MIN_DIM, w));
      h = Math.min(MAX_DIM, Math.max(MIN_DIM, h));
      var cr = this.el.cropImg.getBoundingClientRect();
      if (cr.width) this.state.savedCrop = { x: this.crop.x / cr.width, y: this.crop.y / cr.height, w: this.crop.w / cr.width, h: this.crop.h / cr.height };
      this.state.cropRatio = this.crop.w / this.crop.h;

      /* The size follows the crop. Not silent: these are the exact dimensions
         shown on the confirm button before it was pressed, and the fields below
         update to match so the customer can see what they now have. */
      this.state.w = w; this.state.h = h; this.state.sizeTouched = true;
      this.state.fitMode = 'crop';
      this.$$('.preset').forEach(function (b) { b.classList.remove('selected'); });
      var customBtn = this.querySelector('[data-custom]');
      if (customBtn) customBtn.classList.add('selected');
      this.el.customFields.classList.add('open');
      this.el.cw.value = this.state.w; this.el.ch.value = this.state.h;

      /* Awaited, and BEFORE closeCrop() — while the dialog is still open the
         customer can't reach Add to Cart at all, which is what actually
         closes the race: this used to fire-and-forget and close the dialog
         immediately, so confirming a crop and clicking Add to Cart quickly
         could submit before the async render (image decode -> canvas draw
         -> toBlob) finished, silently missing the cropped file. Flagged by
         Colin, 2026-08-13. */
      await this.refreshCroppedUpload();

      this.closeCrop();
      /* Re-evaluate rather than blanket-hiding: if the crop still doesn't match
         the ordered size, the warning must stay up. */
      this.checkAspect();

      this.el.upStatus.textContent = '✓ Uploaded · sized ' + this.state.w + ' × ' + this.state.h + '″';
      this.render();
    }

    /* Writes current state into the hidden line-item-property inputs right
       before the surrounding <product-form> submits. Real artwork URL/thumb
       and the actual charged price are NOT set here — see file header.

       Artwork status and fit mode are translated to plain-English labels
       here, at the boundary, rather than changing what state.sendLater /
       state.fitMode hold internally — the rest of the app still compares
       against 'stretch' / 'crop' / 'unverified' directly (see checkAspect,
       openCrop, confirmCrop), so only the outgoing property value changes.
       Flagged by a client tester as "eliminate the coding language",
       2026-08-12. */
    /* An optional field left blank still submits its key, so every order that
       skipped them showed bare "Project Details" and "Instagram" rows in admin
       with nothing after them. The cart already hides blank properties; admin
       deliberately shows everything, so the only way to keep it clean is not to
       send them. Dropping the `name` keeps the field out of the form post
       without touching what the customer sees or typed; it is restored the
       moment they type into it again, so a failed submit can't lose the field. */
    dropBlankOptionalProps(){
      var self = this;
      ['dname', 'pinfo', 'ig'].forEach(function (ref) {
        var el = self.el[ref] || self.querySelector('[data-ref="' + ref + '"]') || self.querySelector('[data-role="' + ref + '"]');
        if (!el) return;
        if (el.value && el.value.trim()) {
          if (el.dataset.propName) { el.name = el.dataset.propName; delete el.dataset.propName; }
        } else if (el.name) {
          el.dataset.propName = el.name;
          el.removeAttribute('name');
        }
      });
    }

    syncProperties(){
      this.dropBlankOptionalProps();
      /* "Inches" is spelled out in the value itself (not just implied by the
         property label) per Colin's exact requested format, 2026-08-12 — see
         line-item.liquid's extraction of pricer_width/pricer_height, which
         strips this suffix back off before building the customer-facing
         "15 × 10″" cart text, so it doesn't end up duplicated there. */
      if (this.el.propWidth) this.el.propWidth.value = this.state.w ? this.state.w + ' Inches' : '';
      if (this.el.propHeight) this.el.propHeight.value = this.state.h ? this.state.h + ' Inches' : '';
      if (this.el.propArtStatus) this.el.propArtStatus.value = this.state.sendLater ? 'Send later' : (this.state.artUploaded ? 'Uploaded' : '');
      /* _Crop Data (internal) removed 2026-08-20 per Colin — state.savedCrop is
         still what bakeCrop renders _Cropped Artwork Preview from, it just no
         longer rides along as raw JSON on the order. */
      if (this.el.propFitMode) {
        var fitLabels = { stretch: 'Stretched to fit', crop: 'Cropped to fit', unverified: 'Unverified proportions' };
        this.el.propFitMode.value = fitLabels[this.state.fitMode] || '';
      }
    }
  }

  customElements.define('pricer-app', PricerApp);
})();
